/**
 * Maillage interne — mapping centralisé des liens contextuels par topic.
 *
 * Objectif SEO : augmenter le temps de session, le crawl, et l'autorité
 * thématique en proposant systématiquement 3-5 liens pertinents en bas
 * de chaque page éducative.
 *
 * Convention :
 *  - clé   : slug "topic" (kebab-case) — pas une URL.
 *  - liens : { label, path, kicker, intent }
 *      - kicker  : étiquette éditoriale courte (ex. "Officiel", "Plateau", "Sécurité")
 *      - intent  : 'reviser' | 'comprendre' | 'pratiquer' | 'video' | 'reference'
 *
 * Pour ajouter un nouveau topic, suis ce schéma. Pour intégrer dans une
 * page, utilise `<RelatedLinks topic="casque" />`.
 */

const L = (label, path, kicker, intent = 'comprendre') => ({ label, path, kicker, intent });

export const RELATED_LINKS_MAP = {
  // ============================================================
  // CASQUE / ÉQUIPEMENT
  // ============================================================
  casque: [
    L('Équipement obligatoire à moto', '/equipement-moto-obligatoire', 'EPI', 'reference'),
    L("L'environnement & la sécurité", '/revision?theme=E', 'Réviser · Thème E', 'reviser'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
    L('Gants moto homologués CE', '/gants-moto-homologues', 'EPI', 'comprendre'),
    L('Vidéos sécurité officielles', '/videos', 'Vidéo', 'video'),
  ],
  epi: [
    L('Casque moto · norme ECE 22-06', '/casque-moto-ece-22-06', 'EPI', 'comprendre'),
    L('Gants moto homologués', '/gants-moto-homologues', 'EPI', 'comprendre'),
    L('Équipements de protection · Thème P', '/revision?theme=P', 'Réviser', 'reviser'),
    L("L'environnement · Thème E", '/revision?theme=E', 'Réviser', 'reviser'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
  ],
  gants: [
    L("Casque moto homologué", '/casque-moto-ece-22-06', 'EPI', 'comprendre'),
    L('Équipement obligatoire complet', '/equipement-moto-obligatoire', 'Référence', 'reference'),
    L('Équipements de protection · Thème P', '/revision?theme=P', 'Réviser', 'reviser'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
  ],

  // ============================================================
  // PLATEAU
  // ============================================================
  plateau: [
    L('Allure lente · technique pas à pas', '/magazine/epreuve-plateau-moto-a1-a2', 'Plateau', 'comprendre'),
    L('Trajectoires de sécurité', '/trajectoires-moto-securite', 'Conduite', 'comprendre'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
    L("Permis A1 / A2 — déroulement", '/permis-a1-a2', 'Formation', 'reference'),
    L('Mécanique & équipements · Thème M', '/revision?theme=M', 'Réviser', 'reviser'),
  ],
  trajectoires: [
    L('Plateau · 6 manœuvres officielles', '/epreuves/plateau', 'Plateau', 'pratiquer'),
    L('Distances de sécurité', '/distances-securite-moto', 'Sécurité', 'comprendre'),
    L('La route · Thème R', '/revision?theme=R', 'Réviser', 'reviser'),
    L('La circulation · Thème C', '/revision?theme=C', 'Réviser', 'reviser'),
    L('Parcours pédagogique guidé', '/parcours', 'Méthode', 'comprendre'),
  ],

  // ============================================================
  // ETM / EXAMEN / RÉVISION
  // ============================================================
  etm: [
    L("Programme officiel ETM 2026", '/programme-etm', 'Référence', 'reference'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
    L("Réviser par thème (9 thèmes officiels)", '/revision', 'Réviser', 'reviser'),
    L('Parcours pédagogique guidé', '/parcours', 'Méthode', 'comprendre'),
    L('Lexique du Code Moto', '/programme-etm#lexique', 'Lexique', 'reference'),
  ],
  revision: [
    L("Programme officiel ETM 2026", '/programme-etm', 'Référence', 'reference'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
    L('Parcours pédagogique guidé', '/parcours', 'Méthode', 'comprendre'),
    L('Plateau · 6 manœuvres officielles', '/epreuves/plateau', 'Plateau', 'pratiquer'),
    L('Lexique du Code Moto', '/programme-etm#lexique', 'Lexique', 'reference'),
  ],

  // ============================================================
  // PERMIS A1 / A2 / PASSERELLE / 125
  // ============================================================
  'permis-a1-a2': [
    L("Programme officiel ETM 2026", '/programme-etm', 'Référence', 'reference'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
    L('Plateau · 6 manœuvres officielles', '/epreuves/plateau', 'Plateau', 'pratiquer'),
    L('Casque moto · norme ECE 22-06', '/casque-moto-ece-22-06', 'EPI', 'comprendre'),
    L('Toutes les formations 2 & 3 roues', '/formation', 'Formation', 'reference'),
  ],
  'passerelle-a2-a': [
    L('Permis A2 — pilier complet', '/permis-a2', 'Permis', 'reference'),
    L('Permis A1 — pilier complet', '/permis-a1', 'Permis', 'reference'),
    L('Toutes les formations 2 & 3 roues', '/formation', 'Formation', 'reference'),
    L('Programme officiel ETM 2026', '/programme-etm', 'Référence', 'reference'),
    L('Plateau · 6 manœuvres officielles', '/epreuves/plateau', 'Plateau', 'pratiquer'),
  ],
  '125': [
    L('125 / permis B — pilier complet', '/125-permis-b', 'Pilier', 'reference'),
    L('Permis A1 — pilier complet', '/permis-a1', 'Permis', 'reference'),
    L('Toutes les formations 2 & 3 roues', '/formation', 'Formation', 'reference'),
    L('Casque moto · norme ECE 22-06', '/casque-moto-ece-22-06', 'EPI', 'comprendre'),
    L('Programme officiel ETM 2026', '/programme-etm', 'Référence', 'reference'),
  ],
  'circulation-moto': [
    L('Trajectoires de sécurité', '/trajectoires-moto-securite', 'Conduite', 'comprendre'),
    L('Distances de sécurité', '/distances-securite-moto', 'Sécurité', 'comprendre'),
    L('Plateau · 6 manœuvres officielles', '/epreuves/plateau', 'Plateau', 'pratiquer'),
    L('La circulation · Thème C', '/revision?theme=C', 'Réviser', 'reviser'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
  ],
  'l5e-ryker': [
    L('Permis A1 — pilier complet', '/permis-a1', 'Permis', 'reference'),
    L('Toutes les formations 2 & 3 roues', '/formation', 'Formation', 'reference'),
    L('Casque moto · norme ECE 22-06', '/casque-moto-ece-22-06', 'EPI', 'comprendre'),
    L('Mobilités urbaines', '/mobilites', 'Mobilité', 'comprendre'),
    L('Programme officiel ETM 2026', '/programme-etm', 'Référence', 'reference'),
  ],

  // ============================================================
  // BSR / AM / ASSR
  // ============================================================
  'bsr-am': [
    L('ASSR 1 — préparer en 5ᵉ', '/magazine/assr1-preparer-5eme', 'ASSR', 'comprendre'),
    L('ASSR 2 — préparer en 3ᵉ', '/magazine/assr2-preparer-3eme', 'ASSR', 'comprendre'),
    L('Toutes les formations 2 & 3 roues', '/formation', 'Formation', 'reference'),
    L('Casque moto · norme ECE 22-06', '/casque-moto-ece-22-06', 'EPI', 'comprendre'),
  ],
  assr: [
    L('Permis AM (BSR) — déroulement', '/formation', 'Permis AM', 'reference'),
    L('Permis A1 / A2 — passer le permis', '/permis-a1-a2', 'Permis', 'reference'),
    L('Programme officiel ETM 2026', '/programme-etm', 'Référence', 'reference'),
    L('Casque moto · norme ECE 22-06', '/casque-moto-ece-22-06', 'EPI', 'comprendre'),
  ],

  // ============================================================
  // SÉCURITÉ / RÈGLES SPÉCIFIQUES
  // ============================================================
  alcool: [
    L('Téléphone & oreillettes en moto', '/magazine/telephone-oreillettes-moto', 'Règles', 'comprendre'),
    L('Conducteur · Thème L', '/revision?theme=L', 'Réviser', 'reviser'),
    L('Premiers secours · Thème S', '/revision?theme=S', 'Réviser', 'reviser'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
  ],
  telephone: [
    L('Alcool & stupéfiants — règles 2026', '/alcool-stupefiants-moto', 'Règles', 'comprendre'),
    L('Conducteur · Thème L', '/revision?theme=L', 'Réviser', 'reviser'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
  ],
  zfe: [
    L('Autoroute & voies rapides', '/magazine/autoroute-moto-regles-2026', 'Conduite', 'comprendre'),
    L("L'environnement · Thème E", '/revision?theme=E', 'Réviser', 'reviser'),
    L('La circulation · Thème C', '/revision?theme=C', 'Réviser', 'reviser'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
  ],
  autoroute: [
    L('Distances de sécurité', '/distances-securite-moto', 'Sécurité', 'comprendre'),
    L('Trajectoires de sécurité', '/trajectoires-moto-securite', 'Conduite', 'comprendre'),
    L('La route · Thème R', '/revision?theme=R', 'Réviser', 'reviser'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
  ],
  distances: [
    L('Trajectoires de sécurité', '/trajectoires-moto-securite', 'Conduite', 'comprendre'),
    L('Autoroute & voies rapides', '/magazine/autoroute-moto-regles-2026', 'Conduite', 'comprendre'),
    L('La route · Thème R', '/revision?theme=R', 'Réviser', 'reviser'),
    L('Plateau · 6 manœuvres officielles', '/epreuves/plateau', 'Plateau', 'pratiquer'),
  ],

  // ============================================================
  // FORMATION / PARCOURS / HOME
  // ============================================================
  formation: [
    L("Programme officiel ETM 2026", '/programme-etm', 'Référence', 'reference'),
    L("Réviser par thème (9 thèmes)", '/revision', 'Réviser', 'reviser'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
    L('Plateau · 6 manœuvres officielles', '/epreuves/plateau', 'Plateau', 'pratiquer'),
    L('Parcours pédagogique guidé', '/parcours', 'Méthode', 'comprendre'),
  ],
  parcours: [
    L("Réviser par thème (9 thèmes officiels)", '/revision', 'Réviser', 'reviser'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
    L('Plateau · 6 manœuvres officielles', '/epreuves/plateau', 'Plateau', 'pratiquer'),
    L('Toutes les formations 2 & 3 roues', '/formation', 'Formation', 'reference'),
    L('Programme officiel ETM 2026', '/programme-etm', 'Référence', 'reference'),
  ],
  home: [
    L("Réviser par thème (9 thèmes officiels)", '/revision', 'Réviser', 'reviser'),
    L('Magazine pédagogique · 9 dossiers ETM', '/revision', 'Magazine', 'comprendre'),
    L('Parcours pédagogique guidé', '/parcours', 'Méthode', 'comprendre'),
    L('Plateau · 6 manœuvres officielles', '/epreuves/plateau', 'Plateau', 'pratiquer'),
    L('Toutes les formations 2 & 3 roues', '/formation', 'Formation', 'reference'),
  ],
};

/**
 * Récupère les liens d'un topic. Renvoie [] si inconnu.
 */
export function getRelatedLinks(topic) {
  if (!topic) return [];
  return RELATED_LINKS_MAP[topic] || [];
}
